import { html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { BaseElement } from "../utils/ui-components";
import { MediaItem, MediaItemSegment } from "../data/types";
import { SearchResultMatch } from "../server/media";
import { chevronDownIcon } from "../utils/icons";
import { AudioState } from "./audio-player";

@customElement("segment-list-view")
export class SegmentListView extends BaseElement {
    @property()
    item!: MediaItem;

    @property()
    segments: MediaItemSegment[] = [];

    @property()
    segmentMatches: SearchResultMatch[] = [];

    @property()
    onAudioRequest?: (state: AudioState) => void;

    @state()
    private expandedSegments = new Set<number>();

    private toggleSegment(index: number) {
        if (this.expandedSegments.has(index)) {
            this.expandedSegments.delete(index);
        } else {
            this.expandedSegments.add(index);
        }
        this.requestUpdate();
    }

    render() {
        if (!this.segments?.length) return null;

        const hasMatches = this.segmentMatches.length > 0;

        // If no matches, show expander with all segments
        if (!hasMatches) {
            return html`
                <div class="mt-2">
                    <button @click=${() => this.toggleSegment(-1)} class="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900">
                        Segments (${this.segments.length})
                        <i class="w-5 h-5 icon transition-transform duration-200 ${this.expandedSegments.has(-1) ? "rotate-180" : ""}"
                            >${chevronDownIcon}</i
                        >
                    </button>
                    ${this.expandedSegments.has(-1)
                        ? html`
                              <div class="mt-2">
                                  ${this.segments.map(
                                      (segment) => html`
                                          <segment-view
                                              .item=${this.item}
                                              .segment=${segment}
                                              .segmentMatches=${[]}
                                              .onAudioRequest=${this.onAudioRequest}
                                          ></segment-view>
                                      `
                                  )}
                              </div>
                          `
                        : ""}
                </div>
            `;
        }

        // If we have matches, show matching segments first
        const matchedSegments = this.segments.filter((segment) =>
            this.segmentMatches.some(
                (m) => (m.type === "segment" && m.segmentIndex === segment.index) || (m.type === "subtitle" && m.segmentIndex === segment.index)
            )
        );
        const unmatchedSegments = this.segments.filter(
            (segment) =>
                !this.segmentMatches.some(
                    (m) => (m.type === "segment" && m.segmentIndex === segment.index) || (m.type === "subtitle" && m.segmentIndex === segment.index)
                )
        );

        return html`
            <div class="mt-2">
                ${matchedSegments.map(
                    (segment) => html`
                        <segment-view
                            .item=${this.item}
                            .segment=${segment}
                            .segmentMatches=${this.segmentMatches}
                            .onAudioRequest=${this.onAudioRequest}
                        ></segment-view>
                    `
                )}
                ${unmatchedSegments.length > 0 && !this.expandedSegments.has(-1)
                    ? html`
                          <button
                              @click=${() => this.toggleSegment(-1)}
                              class="w-full mt-2 flex justify-center items-center gap-1 text-gray-400 hover:text-gray-600 transition-colors"
                              aria-label="Show all segments"
                          >
                              <span class="text-sm">All Segments</span>
                              <i class="w-5 h-5 icon">${chevronDownIcon}</i>
                          </button>
                      `
                    : ""}
                ${this.expandedSegments.has(-1)
                    ? html`${unmatchedSegments.map(
                          (segment) => html`
                              <segment-view
                                  .item=${this.item}
                                  .segment=${segment}
                                  .segmentMatches=${[]}
                                  .onAudioRequest=${this.onAudioRequest}
                              ></segment-view>
                          `
                      )}`
                    : ""}
            </div>
        `;
    }
}
